import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import { User } from '../types';

interface LoginModalProps {
  onLoginSuccess: (user: User) => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if google client library is loaded
    if (typeof window !== 'undefined' && (window as any).google?.accounts?.id) {
      try {
        (window as any).google.accounts.id.initialize({
          client_id: (window as any).GOOGLE_CLIENT_ID || '1029384756-dummy.apps.googleusercontent.com',
          callback: async (response: any) => {
            if (response.credential) {
              try {
                setLoading(true);
                const res = await api.loginGoogle(response.credential);
                onLoginSuccess(res.user);
              } catch (err: any) {
                setError(err.message || 'Google sign-in failed');
              } finally {
                setLoading(false);
              }
            }
          },
        });

        const btnContainer = document.getElementById('google-btn-container');
        if (btnContainer) {
          (window as any).google.accounts.id.renderButton(btnContainer, {
            theme: 'outline',
            size: 'large',
            width: 280,
            text: 'continue_with',
          });
        }
      } catch (e) {
        // Ignore Google init failure if client ID not configured
      }
    }
  }, [onLoginSuccess]);

  const handleDemoLogin = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.loginDemo();
      onLoginSuccess(res.user);
    } catch (err: any) {
      setError(err.message || 'Failed to login as demo user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '85vh',
        padding: '24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '84px',
          height: '84px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #a855f7, #ec4899)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '44px',
          boxShadow: '0 12px 28px rgba(168, 85, 247, 0.35)',
          marginBottom: '20px',
        }}
      >
        ⭐
      </div>

      <h1 style={{ fontSize: '32px', fontWeight: 900, color: '#1e1b4b', marginBottom: '8px' }}>
        Quest Tracker
      </h1>
      <p style={{ fontSize: '15px', fontWeight: 600, color: '#64748b', maxWidth: '340px', marginBottom: '32px' }}>
        A playful activity tracker for kids. Complete daily quests, earn star rewards, and build streaks!
      </p>

      {error && (
        <div
          style={{
            padding: '10px 16px',
            background: '#fee2e2',
            color: '#dc2626',
            borderRadius: '14px',
            fontSize: '13px',
            fontWeight: 700,
            marginBottom: '18px',
            maxWidth: '320px',
          }}
        >
          {error}
        </div>
      )}

      {/* Google Sign-in button container */}
      <div id="google-btn-container" style={{ marginBottom: '14px', minHeight: '44px' }}>
        {/* Rendered by Google Identity Services or fallback button */}
        <button
          id="btn-google-login-fallback"
          className="pill-btn"
          onClick={() => {
            // Prompt simulated or OAuth flow
            handleDemoLogin();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: 800,
            borderRadius: '16px',
            border: '2px solid #e2e8f0',
            background: '#ffffff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
            />
            <path
              fill="#34A853"
              d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
            />
            <path
              fill="#FBBC05"
              d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.98 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
            />
            <path
              fill="#EA4335"
              d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
            />
          </svg>
          Sign in with Google
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '14px 0', width: '280px' }}>
        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
        <span style={{ fontSize: '12px', fontWeight: 700, color: '#94a3b8' }}>OR</span>
        <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
      </div>

      <button
        id="btn-demo-login"
        onClick={handleDemoLogin}
        disabled={loading}
        className="btn-primary-gradient"
        style={{
          width: '280px',
          background: 'linear-gradient(135deg, #8b5cf6, #3b82f6)',
          fontSize: '15px',
          padding: '12px 20px',
        }}
      >
        {loading ? 'Starting...' : '⚡ Explore as Demo Family'}
      </button>

      <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '16px' }}>
        Includes Leo 🦁, Mia 🦄, and tt 🐲 with sample quests and rewards!
      </p>
    </div>
  );
};
